import { isVideoMime, MAX_VIDEO_BYTES, VIDEO_MIMES, type PresignResponse } from "@klantpunt/shared";
import { idb } from "../offline/db";
import { queue } from "../offline/queue";
import { api, ApiError } from "./api";
import { putToStorage } from "./upload";

/** MIME-type van een video; sommige telefoons geven geen type mee, dan op extensie. */
function videoMime(file: File): string | null {
  if (isVideoMime(file.type)) return (VIDEO_MIMES as readonly string[]).includes(file.type) ? file.type : null;
  if (file.type) return null;
  const ext = file.name.toLowerCase().split(".").pop();
  return ext === "mp4" || ext === "m4v"
    ? "video/mp4"
    : ext === "mov"
      ? "video/quicktime"
      : ext === "webm"
        ? "video/webm"
        : null;
}

/** Video's gaan ongewijzigd mee; alleen de grootte en het type worden vooraf gecontroleerd. */
function checkVideo(file: File): string {
  const mime = videoMime(file);
  if (!mime) throw new ApiError(400, "unsupported_type", "Alleen video's als MP4, MOV of WebM");
  if (file.size > MAX_VIDEO_BYTES)
    throw new ApiError(400, "too_large", `Video is te groot (max ${Math.round(MAX_VIDEO_BYTES / 1048576)} MB)`);
  return mime;
}

export const isVideoFile = (file: File) => file.type.startsWith("video/") || (!file.type && !!videoMime(file));

/** Foto verkleinen naar max 2000 px, JPEG 0,82 (SPEC §11). */
export async function compressPhoto(file: File): Promise<Blob> {
  try {
    const bmp = await createImageBitmap(file, { imageOrientation: "from-image" });
    const s = Math.min(1, 2000 / Math.max(bmp.width, bmp.height));
    const c = document.createElement("canvas");
    c.width = Math.round(bmp.width * s);
    c.height = Math.round(bmp.height * s);
    c.getContext("2d")!.drawImage(bmp, 0, 0, c.width, c.height);
    bmp.close();
    return await new Promise<Blob>((r) => c.toBlob((b) => r(b ?? file), "image/jpeg", 0.82));
  } catch {
    return file;
  }
}

const photoMime = (b: Blob) => (b.type === "image/png" || b.type === "image/webp" ? b.type : "image/jpeg");

/** Direct uploaden via presigned URL (alleen online); geeft het file-ID terug. */
export async function uploadPhoto(file: File): Promise<string> {
  const blob = await compressPhoto(file);
  const id = crypto.randomUUID();
  const p = await api<PresignResponse>("/files/presign", {
    method: "POST",
    body: { id, kind: "photo", mime: photoMime(blob), size: blob.size, name: file.name },
  });
  await putToStorage(p, blob);
  await api(`/files/${id}/confirm`, { method: "POST", body: {} });
  return id;
}

/** Foto's en video's die (nog) alleen op dit apparaat staan: tonen vanuit het geheugen. */
const local = new Map<string, string>();
const localVideo = new Set<string>();

export function registerLocalPhoto(fileId: string, blob: Blob, mime = blob.type) {
  if (!local.has(fileId)) local.set(fileId, URL.createObjectURL(blob));
  if (isVideoMime(mime)) localVideo.add(fileId);
}

/** Na het opstarten: foto's die nog in de wachtrij staan weer zichtbaar maken. */
export async function restoreLocalPhotos() {
  try {
    for (const b of await (await idb()).getAll("blobs")) registerLocalPhoto(b.fileId, b.blob, b.mime);
  } catch {
    /* geen IndexedDB */
  }
}

/**
 * Foto of video via de offline-wachtrij: op dit apparaat bewaard (foto's verkleind) en geüpload zodra het kan.
 * Geeft meteen het (door de client gekozen) file-ID terug.
 */
export async function queuePhoto(file: File, buildingId: string | null): Promise<string> {
  const video = isVideoFile(file);
  const mime = video ? checkVideo(file) : null;
  const blob = video ? file : await compressPhoto(file);
  const fileId = crypto.randomUUID();
  const type = mime ?? photoMime(blob);
  await (await idb()).put("blobs", { fileId, blob, mime: type });
  registerLocalPhoto(fileId, blob, type);
  void queue
    .enqueue({
      buildingId,
      label: video ? "Video" : "Foto",
      req: {
        kind: "upload",
        fileId,
        mime: type,
        name: file.name || (video ? "video.mp4" : "foto.jpg"),
        size: blob.size,
      },
    })
    .catch(() => undefined);
  return fileId;
}

/** Video? Op basis van het MIME-type van de server, of van de lokale (nog niet geüploade) kopie. */
export const isVideo = (fileId: string, mime?: string | null) => isVideoMime(mime) || localVideo.has(fileId);

export const thumbUrl = (fileId: string) => local.get(fileId) ?? `/api/files/${fileId}/url?variant=thumb`;
export const photoUrl = (fileId: string) => local.get(fileId) ?? `/api/files/${fileId}/url`;
